<template>
  <div class="flex flex-col justify-between flex-1" :class="[fullScreen ? 'h-full overflow-auto' : '']">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <Form
          v-slot="{ values, errors }"
          :initial-values="initial_values"
          :validation-schema="schema"
          class="relative mt-2 space-y-4"
          @submit="editPlan">
          <div :class="[`${fullScreen ? '-mt-20' : '-mt-20'}`]" class="absolute z-10 flex items-center gap-2 right-4">
            <div v-if="inPlaybook" :class="[{ 'mt-6': inPlaybook }]" class="flex items-center mr-6 gap-x-2">
              <Switch
                v-model="auto_compute_status"
                :class="[
                  auto_compute_status ? 'bg-blue' : 'bg-gray-200',
                  'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                ]">
                <span
                  aria-hidden="true"
                  :class="[
                    auto_compute_status && $store.state.portfolio.ui_options?.language == 'ar'
                      ? '-translate-x-5'
                      : auto_compute_status
                        ? 'translate-x-5'
                        : 'translate-x-0',
                    'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                  ]" />
              </Switch>
              <span class="dark:text-common-grey">{{ $t("Auto Compute Status") }}</span>
            </div>

            <div v-else>
              <ToggleStatusButton
                :object="plan"
                :prevent-request="true"
                @dynamic-status-changed="
                  ($event) => {
                    auto_compute_status = $event
                  }
                "
                @status-changed="
                  ($event) => {
                    status = $event
                  }
                " />
            </div>
            <div
              v-if="$route.name !== 'WorkspaceItem' && isModuleActivated(MODULES?.WORKSPACE) && !inPlaybook"
              :class="[{ 'mt-3': plan.workspaces_shared_in?.length }]">
              <div v-if="selected_companies?.length < 2" type="button" class="primary-outline-btn w-fit" @click="sharePlan(plan)">
                <Share2 class="w-6 h-6 mr-1 transition ease-in-out duration-side-menu-transition-duration" />
                {{ $t("Share") }}
              </div>
              <div v-if="plan.workspaces_shared_in?.length" class="text-xs text-common-grey">
                {{ $t("Shared in") }} {{ plan.workspaces_shared_in?.length }} {{ $t("workspace") }}
              </div>
            </div>

            <div
              v-if="!editing_disabled && !inPlaybook"
              class="flex items-center justify-center w-8 h-8 bg-white border rounded-full cursor-pointer has-tooltip dark:bg-dark-screen-background dark:border-dark-divider-border"
              @click="handleExpandPlan(plan, values)">
              <Maximize2 class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" color="#2DA7D7" />
              <span
                class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                {{ fullScreen ? $t("Minimize") : $t("Full Screen") }}</span>
            </div>
            <div
              v-if="!inPlaybook"
              class="flex items-center justify-center w-8 h-8 ml-2 bg-white border rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
              @click="handleClosePlan">
              <X color="#FF0000" class="w-6 h-6 transition ease-in-out duration-side-menu-transition-duration" /><span
                class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                {{ $t("Close") }}</span>
            </div>
          </div>

          <div class="mt-10 -space-y-px rounded-md shadow-sm">
            <TextInput
              ref="planName"
              autocomplete="on"
              :autocomplete-options="resultReferencesListForText"
              :disabled="editing_disabled"
              :validate-on-input="true"
              name="name"
              :auto-focus="true"
              :is-box-background="true"
              type="text"
              :label="$t('Plan name')"
              :value="plan.name"
              input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              @update:modelValue="
                ($event) => {
                  plan_name = $event
                }
              " />
          </div>

          <div class="mt-5 border rounded-md dark:border-dark-divider-border h-28">
            <TextEditor
              ref="quill"
              v-model:content="plan_description"
              :value="plan_description"
              name="description"
              :label="$t('Description')"
              content-type="html"
              input-classes="rounded-md"
              :mention-items="resultReferencesListForText"
              theme="bubble"
              toolbar="essential" />
          </div>

          <div class="flex flex-row items-center justify-between gap-2">
            <SelectInput
              :disabled="editing_disabled"
              name="type"
              :items="types"
              label-attr="label"
              :allow-null="false"
              :classes="'w-full'"
              value-attr="value"
              :label="$t('Plan type')"
              :show-label="true"
              :is-box-background="true"
              input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
              @update:currentItem="
                ($event) => {
                  plan_type = $event
                }
              " />
            <div class="relative w-full">
              <label
                for="start_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  inputStartDateIsFocused || !!start_date ? 'top-0' : 'top-1/2',
                  isBoxBackgroud
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  inputStartDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Start date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  ref="start_date"
                  v-model="start_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  mode="date"
                  :popover="{ visibility: 'click' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="start_date"
                        :value="inputValue"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackgroud
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusStartDateInput"
                        @focusout="focusStartDateInput"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            start_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                  </template>
                  <template #footer>
                    <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                      <button v-if="start_date" type="button" class="cancel-btn inline-block" @click.stop="emptyStartDate">
                        {{ $t("Clear") }}
                      </button>
                      <button type="button" class="cancel-btn inline-block" @click.stop="clearDate">{{ $t("Cancel") }}</button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
            <div class="relative w-full">
              <label
                for="update_plan_d_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  !!relative_date_text || inputDueDateIsFocused || !!due_date ? 'top-0' : 'top-1/2',
                  isBoxBackgroud
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  inputDueDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Due date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  ref="due_date"
                  v-model="due_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  mode="date"
                  :popover="{ visibility: 'none' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="update_plan_d_date"
                        :disabled="editing_disabled"
                        autocomplete="off"
                        :value="inputValue ? inputValue : relative_date_text"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackgroud
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusDueDateInput"
                        @focusout="focusDueDateInput"
                        @click="is_modal_relative_date_open = true"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            due_date = $event.target.value
                          }, 800)
                        ">
                    </div>
                    <div v-if="is_modal_relative_date_open" class="absolute z-20">
                      <DateTimeFrame
                        class="w-full mr-2 bg-white rounded-lg dark:bg-dark-box-background"
                        :relative-date="relative_date"
                        @done="
                          ($event) => {
                            due_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @relative-date-changed="
                          ($event) => {
                            relative_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @cancel="is_modal_relative_date_open = false" />
                    </div>
                  </template>

                  <template #footer>
                    <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                      <button v-if="due_date || relative_date" type="button" class="cancel-btn inline-block" @click.stop="emptyDueDate">
                        {{ $t("Clear") }}
                      </button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div :class="[{ '!mt-6': inPlaybook }]" class="flex items-center mt-0 mr-6 gap-x-2">
            <Switch
              v-model="auto_set_start_date"
              :class="[
                auto_set_start_date ? 'bg-blue' : 'bg-gray-200',
                'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  auto_set_start_date && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : auto_set_start_date
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                ]" />
            </Switch>
            <span class="dark:text-common-grey">{{ $t("Auto Set Start Date") }}</span>
          </div>

          <div>
            <div class="w-[800px] mt-3 mb-5">
              <CompaniesSelect
                :short-king="inPlaybook"
                :disabled="isTemplate"
                :label="$t('Add Companies/Opportunities')"
                :companies="selected_companies"
                :autocomplete-options="resultReferencesList"
                @changed="
                  ($event) => {
                    handleSetConmpaniesList($event)
                  }
                " />
            </div>
          </div>

          <div class="flex w-full">
            <div class="w-1/2 mt-3 mb-5">
              <div class="w-1/2 mt-3 mb-5">
                <TeamMembersSelect
                  v-model:selected-team-members="selected_team_members"
                  :autocomplete-options="autocompleteOptionsInPlaybook"
                  :disabled="editing_disabled || isTemplate"
                  :label="$t('Team Members')" />
              </div>
            </div>
            <div v-if="!inPlaybook" class="w-1/2 mt-3 mb-5">
              <ContactsSelect
                v-model:selected-contacts="selected_contacts"
                :disabled="editing_disabled || isTemplate"
                :companies="selected_companies"
                :label="$t('Contacts')"
                :types="['contacts']" />
            </div>
          </div>

          <div
            v-if="inPlaybook || isTemplate"
            class="cursor-pointer"
            @click="assign_companies_to_goals_and_tasks = !assign_companies_to_goals_and_tasks">
            <span class="mr-2 mt-1 text-common-grey">{{ $t("Assign selected companies to tasks and goals") }} </span>
            <input
              :disabled="true"
              :checked="assign_companies_to_goals_and_tasks"
              type="checkbox"
              class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
          </div>

          <div
            v-if="inPlaybook || isTemplate"
            class="cursor-pointer"
            @click="assign_collaborators_to_goals_and_tasks = !assign_collaborators_to_goals_and_tasks">
            <span class="mt-1 mr-2 text-common-grey">{{ $t("Assign selected team members to tasks and goals") }} </span>
            <input
              :disabled="true"
              :checked="assign_collaborators_to_goals_and_tasks"
              type="checkbox"
              class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
          </div>

          <div class="flex items-center gap-x-2">
            <label class="relative text-common-grey top-4">{{ $t("Assigned goals") }}:</label>

            <div :class="{ 'pointer-events-none opacity-0': inPlaybook }" class="ml-auto primary-btn w-fit" @click="handleFilterGoals">
              <Funnel class="w-5 h-5 mr-2 text-white" />{{ goals_filter_active ? $t("Clear filter") : $t("Filter goals") }}
            </div>

            <div
              v-if="!isTemplate && !inPlaybook"
              class="btn w-fit primary-btn"
              :class="[`${goals_filter_active ? 'bg-gray-300' : ''} `]"
              @click="!isTemplate && !inPlaybook ? handleCreateGoal() : null">
              {{ $t("Create New Goal") }}
            </div>

            <Menu
              v-if="!editing_disabled && (isTemplate || inPlaybook)"
              v-slot="{ close }"
              ref="goalsMenu"
              as="div"
              class="relative flex-shrink-0"
              :class="[`${filter_applied ? 'pointer-events-none' : ''} `]">
              <MenuButton
                class="inline-flex items-center justify-center rounded-full w-36 border-1 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                <div class="btn primary-btn" :class="[`${filter_applied ? 'bg-gray-300' : ''} `]">
                  {{ $t("Add Goal to Plan") }}
                </div>
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems class="absolute z-50 w-40 mx-3 mt-3 -ml-48 origin-bottom-right bg-transparent border-0 focus:outline-none">
                  <div class="py-1 bg-white dark:bg-dark-screen-background p-3 border dark:border-dark-divider-border rounded-lg w-[340px]">
                    <div>
                      <div v-if="plan.template_id || isTemplate" class="flex w-full justify-end">
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get == 'goals' }"
                          class="p-1 px-3 rounded-full border cursor-pointer"
                          @click="list_to_get = 'goals'">
                          {{ $t("Goals") }}
                        </div>
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get == 'templates' }"
                          class="p-1 px-3 rounded-full border ml-2 cursor-pointer"
                          @click="list_to_get = 'templates'">
                          {{ $t("Templates") }}
                        </div>
                      </div>

                      <AsyncAutoCompleteInput
                        v-if="isTemplate || inPlaybook"
                        :add="false"
                        :add-label="$t('Create Goal')"
                        :clear-on-select="true"
                        :is-box-background="true"
                        classes="w-full"
                        input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                        name="goal"
                        value-attr="id"
                        :endpoint="
                          list_to_get == 'goals'
                            ? `/api/organizations/${$store.state.organization.id}/goals/${plan.id ? `?target_plan=${plan.id}` : ``}`
                            : `/api/organizations/${$store.state.organization.id}/templates?source_object_type=goal`
                        "
                        type="text"
                        :placeholder="$t('Search for goal to add')"
                        :debounce="500"
                        @chosen="
                          ($event) => {
                            handleGoalSelected($event, close)
                          }
                        " />
                    </div>
                    <div
                      v-if="!inPlaybook"
                      class="mt-3 mb-3 btn primary-btn"
                      @click="
                        () => {
                          close()
                          handleCreateGoal()
                        }
                      ">
                      + {{ $t("Create New Goal") }}
                    </div>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
          <div v-if="goals_filter_active">
            <div class="grid items-center justify-center grid-cols-6 gap-x-4">
              <div>
                <MultiSelectInput
                  v-model:current-items="goals_status"
                  name="goals_status"
                  :items="$store.state.data?.goal_statuses"
                  label-attr="name"
                  value-attr="value"
                  :allow-null="true"
                  :label="$t('Status')"
                  classes="w-full"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  :selection-text="null"
                  :placeholder="$t('Status')"
                  :show-label="false" />
              </div>

              <div class="flex w-full ml-auto rounded-md shadow-sm">
                <div class="relative flex items-stretch flex-grow focus-within:z-10">
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <TextInput
                    :value="search_term"
                    input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                    name="goal_name"
                    type="text"
                    :validate-on-input="true"
                    :label="$t('Goal name')"
                    @update:modelValue="
                      (e) => {
                        search_term = e
                      }
                    " />
                </div>
              </div>

              <div>
                <AsyncMultiSelectCompaniesInput
                  v-if="isTemplate || inPlaybook"
                  :add-label="$t('Companies')"
                  :clear-on-select="true"
                  :is-box-background="true"
                  classes="w-full"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                  name="companies"
                  value-attr="id"
                  :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
                  endpoint-method="post"
                  search-field="name"
                  type="text"
                  :placeholder="$t('Companies')"
                  @update:currentItems="
                    ($event) => {
                      handleGoalsCompaniesSelected($event)
                    }
                  " />
              </div>

              <div>
                <AsyncMultiSelectContactsInput
                  :add-label="$t('Contacts')"
                  :clear-on-select="true"
                  :is-box-background="true"
                  classes="w-full"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                  name="companies"
                  value-attr="id"
                  :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
                  endpoint-method="post"
                  search-field="name"
                  type="text"
                  :placeholder="$t('Contacts')"
                  @update:currentItems="
                    ($event) => {
                      handleGoalsContactsSelected($event)
                    }
                  " />
              </div>

              <div>
                <MultiSelectInput
                  v-model:current-items="goals_assigned_to"
                  name="goals_assigned_to"
                  :items="$store.state.data?.users"
                  label-attr="full_name"
                  :allow-null="true"
                  value-attr="id"
                  label="Users"
                  classes="w-full dark:text-dark-body-text"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  selection-text="Members (%COUNT)"
                  :placeholder="$t('Team members')"
                  :show-label="false" />
              </div>

              <div>
                <DatePicker
                  v-model="goals_due_date_range"
                  :is-dark="$store.state.ui.darkMode"
                  :input-debounce="500"
                  mode="range"
                  class="ml-auto bg-white dark:bg-dark-screen-background"
                  :attributes="[
                    {
                      key: 'today',
                      dot: true,
                      dates: new Date()
                    }
                  ]"
                  :popover="{ visibility: 'click' }"
                  is-range
                  range>
                  <template #default="{ inputValue, inputEvents, hidePopover }">
                    <div class="relative flex-grow">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <button
                        v-if="goals_due_date_range && goals_due_date_range.start"
                        class="absolute right-0 flex items-center justify-center w-6 h-6 mx-2 rounded-full top-2 group hover:bg-gray-300 rounderd-full"
                        type="button"
                        @click="goals_due_date_range = null">
                        <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
                      </button>

                      <input
                        :value="goals_due_date_range && goals_due_date_range.start ? `${inputValue.start} - ${inputValue.end}` : ''"
                        :placeholder="$t('Due date')"
                        class="w-full px-10 py-2 pl-8 pr-2 text-gray-900 bg-white border border-gray-300 rounded outline-none dark:bg-dark-box-background dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light"
                        @blur="hidePopover"
                        v-on="inputEvents.start || inputEvents.end"
                        @input="
                          $utils.debounce(() => {
                            goals_due_date_range = $event.target.value
                          }, 300)
                        ">
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div
            v-if="!related_goals_displayed?.length"
            class="relative w-full p-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
            <div class="text-lg">
              {{ $t("No goals to display") }}
            </div>
            <div v-if="!filter_applied" class="mt-1 text-sm">
              {{ $t("Add new goals to plan") }}
            </div>
            <div v-else class="mt-1 text-sm">
              {{ $t("No goals matching filter") }}
            </div>
          </div>

          <div>
            <draggable
              v-model="related_goals_displayed"
              as="div"
              static
              :class="[
                'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
                `grid ${fullScreen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8 overflow-visible mt-1 border-0 ${
                  $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
              ]"
              :component-data="{ type: 'transition-group' }"
              v-bind="{
                disabled: goals_count > perPage,
                animation: 200,
                group: 'group',
                ghostClass: 'ghost',
                chosenClass: 'chosen'
              }"
              item-key="id"
              @end="endGoalDrag()">
              <template #item="{ element: goal }">
                <div class="bg-white">
                  <div>
                    <div
                      v-if="expanded_goals.includes(goal.id)"
                      class="relative p-4 mb-4 bg-white border rounded-md dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleGoalEnter(goal)"
                      @mouseleave="handleGoalLeave">
                      <div class="flex items-center">
                        <ToggleStatusButton
                          :object="goal"
                          @statusChanged="
                            ($event) => {
                              handleStatusChanged($event, goal)
                            }
                          " />

                        <div class="flex ml-2">
                          <div>{{ goal.name }}</div>
                          <Badge :icon="task_icon" :object="goal" :list-name="'tasks'" />
                        </div>
                        <div class="absolute flex -right-3 -top-3 gap-x-2">
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleExpandGoal(goal)">
                            <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                            <span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Minimize") }}</span>
                          </div>
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                            @click="handleViewGoal(goal)">
                            <Maximize2
                              class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                              color="#2DA7D7" />
                            <span
                              class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                              {{ $t("View Goal") }}</span>
                          </div>
                          <div
                            v-if="!editing_disabled"
                            class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleRemoveGoal(goal)">
                            <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Remove") }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex w-full mt-5">
                        <div v-if="goal.id && !goal.for_template" class="mt-2 w-[40%]">
                          <div class="flex">
                            <div class="flex gap-x-2">
                              <div v-if="goal?.filter" class="text-common-grey">
                                {{ $t("Progress") }}:
                              </div>
                              <div v-else class="text-sm text-common-grey">
                                {{ $t("No filter specified for this goal") }}
                              </div>
                              <div v-if="goal?.filter" class="text-sm font-light text-md dark:text-white">
                                {{
                                  goal?.record.values[goal.filter.split(" ")[1]]
                                    ? $utils.truncateNumber(parseInt(goal?.record.values[goal.filter.split(" ")[1]]))
                                    : 0
                                }}/{{ $utils.truncateNumber(parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) }}
                                {{ $utils.getMetricByReferenceName(goal.filter.split(" ")[1])?.name }}
                              </div>
                            </div>
                            <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-lg">
                              {{
                                Math.round(
                                  (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                                    parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) *
                                    100
                                )
                              }}%
                            </div>
                          </div>

                          <div v-if="goal.filter" class="py-4 -mt-3 text-sm text-gray-500 whitespace-nowrap">
                            <div class="w-full h-2 bg-gray-300 rounded-full dark:bg-dark-box-background">
                              <div
                                :style="{
                                  width: `${
                                    (goal?.record.values[goal.filter.split(' ')[1]] ? goal?.record.values[goal.filter.split(' ')[1]] : 0) /
                                    goal.filter.split(' ')[goal.filter.split(' ').length - 2] >
                                    1
                                      ? 100
                                      : `${
                                        (goal?.record.values[goal.filter.split(' ')[1]]
                                          ? goal?.record.values[goal.filter.split(' ')[1]]
                                          : 0) / goal.filter.split(' ')[goal.filter.split(' ').length - 2]
                                      }` * 100
                                  }%`
                                }"
                                :class="[` rounded-full bg-blue h-2`]" />
                            </div>
                          </div>
                        </div>
                        <div class="ml-20 w-[10%]">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey whitespace-nowrap">
                              {{ $t("Due Date") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <span v-if="goal.due_date">
                                {{ $moment(goal.due_date).format("DD MMM  YYYY") }}
                              </span>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>
                        <div :class="[`${goal?.assigned_to.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                          <div
                            :class="[`${goal?.assigned_to.length > 4 ? '' : '-ml-10'}`]"
                            class="mb-2 text-common-grey whitespace-nowrap text-start">
                            {{ $t("Team Members") }}
                          </div>
                          <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                            <span v-for="user in goal?.assigned_to.slice(0, 4)" :key="user.id" class="inline-block has-tooltip">
                              <img
                                v-if="user.avatar"
                                class="inline-block rounded-full h-9 w-9 object-cover"
                                :src="user.avatar"
                                onerror="this.onerror=null; this.src=window.default_avatar;"
                                alt="">
                              <Avatar v-else :first-name="user.first_name" :last-name="user.last_name" />
                              <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                                <div class="">
                                  <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                                    {{ Record.from(user).full_name }}
                                  </p>
                                </div>
                              </span>
                            </span>
                            <span
                              v-if="goal?.assigned_to.length > 4"
                              class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                              + {{ goal?.assigned_to.length - 4 }} {{ $t("more") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      class="relative flex p-4 mb-4 align-middle bg-white border rounded-md dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleGoalEnter(goal)"
                      @mouseleave="handleGoalLeave">
                      <div
                        :class="[
                          'w-4 h-4 rounded-full mt-1 mr-4 relative',
                          {
                            'bg-status-warning-yellow': getActualStatus(goal, goal.status) == 'overdue',
                            'bg-dark-brand-blue': ['in-progress', 'pending'].includes(goal.status),
                            'bg-status-success-green': goal.status == 'completed',
                            'bg-status-error-red': ['cancelled', 'missed'].includes(goal.status)
                          }
                        ]" />
                      <div class="flex ml-2">
                        <button v-if="!(goals_count > perPage)" class="button hover:cursor-move mr-2">
                          <Tally2 class="h-5 w-5 text-common-grey rotate-90" aria-hidden="true" />
                        </button>

                        <div>{{ goal.name }}</div>
                        <Badge :icon="task_icon" :object="goal" :list-name="'tasks'" />
                      </div>
                      <div v-if="goal.id && !goal.for_template" class="ml-auto mr-20">
                        <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-md text-common-grey">
                          {{
                            Math.round(
                              (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                                parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) *
                                100
                            )
                          }}%
                        </div>
                      </div>
                      <div class="absolute flex -right-3 -top-3 gap-x-2">
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleExpandGoal(goal)">
                          <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                          <span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Expand") }}</span>
                        </div>
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                          @click="handleViewGoal(goal)">
                          <Maximize2
                            class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                            color="#2DA7D7" />
                          <span
                            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                            {{ $t("View Goal") }}</span>
                        </div>
                        <div
                          v-if="!editing_disabled"
                          class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleRemoveGoal(goal)">
                          <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Remove") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>

          <div class="flex items-center w-full !-mt-4">
            <div
              v-if="goals_count > 6 && goals_count != perPage"
              class="mr-2 text-sm text-blue cursor-pointer"
              @click="perPage = goals_count">
              {{ $t("Show all") }} ({{ goals_count }})
            </div>
            <div v-else-if="goals_count > 6" class="mr-2 text-sm text-blue cursor-pointer" @click="perPage = 6">
              {{ $t("Show less") }}
            </div>
            <Pagination
              v-if="goals_count > perPage"
              class="ml-auto"
              :total-pages="total_pages"
              :current-page="currentPage"
              @input="changePage" />
          </div>

          <div class="flex items-center gap-x-2">
            <label class="relative text-common-grey top-4">{{ $t("Assigned tasks") }}:</label>

            <div
              :class="{ 'opacity-100 pointer-events-default': !inPlaybook && (related_tasks_displayed?.length || tasks_filter_active) }"
              class="ml-auto primary-btn w-fit opacity-0 pointer-events-none"
              @click="handleFilterTasks">
              <Funnel class="w-5 h-5 mr-2 text-white" />{{ tasks_filter_active ? $t("Clear filter") : $t("Filter tasks") }}
            </div>

            <div
              v-if="!isTemplate && !inPlaybook"
              class="btn w-fit primary-btn"
              :class="[`${tasks_filter_applied ? 'bg-gray-300' : ''} `]"
              @click="!isTemplate && !inPlaybook ? handleCreateTask() : null">
              {{ $t("Create New Task") }}
            </div>

            <Menu
              v-if="!editing_disabled && (isTemplate || inPlaybook)"
              v-slot="{ close }"
              ref="goalsMenu"
              as="div"
              class="relative flex-shrink-0"
              :class="[
                `${(!related_tasks_displayed?.length && !tasks_filter_active) || inPlaybook ? 'ml-auto' : ''} ${
                  tasks_filter_applied ? 'pointer-events-none' : ''
                } `
              ]">
              <MenuButton
                class="inline-flex items-center justify-center rounded-full w-36 border-1 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                <div
                  class="btn primary-btn"
                  :class="[`${tasks_filter_applied ? 'bg-gray-300' : ''} `]"
                  @click="!isTemplate && !inPlaybook ? handleCreateTask() : null">
                  {{ $t("Add Task to Plan") }}
                </div>
              </MenuButton>

              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems class="absolute z-50 w-40 mx-3 mt-3 -ml-48 origin-bottom-right bg-transparent border-0 focus:outline-none">
                  <div class="py-1 bg-white dark:bg-dark-screen-background p-3 border dark:border-dark-divider-border rounded-lg w-[340px]">
                    <div>
                      <div v-if="plan.template_id || isTemplate" class="flex w-full justify-end">
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get_task == 'tasks' }"
                          class="p-1 px-3 rounded-full border cursor-pointer"
                          @click="list_to_get_task = 'tasks'">
                          {{ $t("Tasks") }}
                        </div>
                        <div
                          :class="{ 'text-white bg-light-brand-blue': list_to_get_task == 'templates' }"
                          class="p-1 px-3 rounded-full border ml-2 cursor-pointer"
                          @click="list_to_get_task = 'templates'">
                          {{ $t("Templates") }}
                        </div>
                      </div>
                      <AsyncAutoCompleteInput
                        v-if="isTemplate || inPlaybook"
                        :add="false"
                        :add-label="$t('Create Task')"
                        :clear-on-select="true"
                        :is-box-background="true"
                        classes="w-full"
                        input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                        name="task"
                        value-attr="id"
                        :endpoint="
                          list_to_get_task == 'tasks'
                            ? `/api/organizations/${$store.state.organization.id}/tasks/${plan.id ? `?target_plan=${plan.id}` : ``}`
                            : `/api/organizations/${$store.state.organization.id}/templates?source_object_type=task`
                        "
                        type="text"
                        :placeholder="$t('Search for task to add')"
                        :debounce="500"
                        @chosen="
                          ($event) => {
                            handleTaskSelected($event, close)
                          }
                        " />
                    </div>
                    <div
                      v-if="!inPlaybook"
                      class="mt-3 mb-3 btn primary-btn"
                      @click="
                        () => {
                          close()
                          handleCreateTask()
                        }
                      ">
                      + {{ $t("Create New Task") }}
                    </div>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>

          <div v-if="tasks_filter_active">
            <div class="grid items-center justify-center grid-cols-6 gap-x-4">
              <div>
                <MultiSelectInput
                  v-model:current-items="tasks_status"
                  name="tasks_status"
                  :items="$store.state.data?.task_statuses"
                  label-attr="name"
                  value-attr="value"
                  :allow-null="true"
                  :label="$t('Status')"
                  classes="w-full"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  :selection-text="null"
                  :placeholder="$t('Status')"
                  :show-label="false" />
              </div>

              <div class="flex w-full ml-auto rounded-md shadow-sm">
                <div class="relative flex items-stretch flex-grow focus-within:z-10">
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <TextInput
                    :value="tasks_search_term"
                    input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                    name="task_name"
                    type="text"
                    :validate-on-input="true"
                    label="Task name"
                    @update:modelValue="
                      (e) => {
                        tasks_search_term = e
                      }
                    " />
                </div>
              </div>

              <div>
                <AsyncMultiSelectCompaniesInput
                  :add-label="$t('Companies')"
                  :clear-on-select="true"
                  :is-box-background="true"
                  classes="w-full"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                  name="companies"
                  value-attr="id"
                  :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
                  endpoint-method="post"
                  search-field="name"
                  type="text"
                  :placeholder="$t('Companies')"
                  @update:currentItems="
                    ($event) => {
                      handleTasksCompaniesSelected($event)
                    }
                  " />
              </div>

              <div>
                <AsyncMultiSelectContactsInput
                  :add-label="$t('Contacts')"
                  :clear-on-select="true"
                  :is-box-background="true"
                  classes="w-full"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                  name="companies"
                  value-attr="id"
                  :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
                  endpoint-method="post"
                  search-field="name"
                  type="text"
                  :placeholder="$t('Contacts')"
                  @update:currentItems="
                    ($event) => {
                      handleTasksContactsSelected($event)
                    }
                  " />
              </div>

              <div>
                <MultiSelectInput
                  v-model:current-items="tasks_assigned_to"
                  name="tasks_assigned_to"
                  :items="$store.state.data?.users"
                  label-attr="full_name"
                  :allow-null="true"
                  value-attr="id"
                  :label="$t('Users')"
                  classes="w-full dark:text-dark-body-text"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  selection-text="Members (%COUNT)"
                  :placeholder="$t('team members')"
                  :show-label="false" />
              </div>

              <div>
                <DatePicker
                  v-model="tasks_due_date_range"
                  :is-dark="$store.state.ui.darkMode"
                  :input-debounce="500"
                  mode="range"
                  class="ml-auto bg-white dark:bg-dark-screen-background"
                  :attributes="[
                    {
                      key: 'today',
                      dot: true,
                      dates: new Date()
                    }
                  ]"
                  :popover="{ visibility: 'click' }"
                  is-range
                  range>
                  <template #default="{ inputValue, inputEvents, hidePopover }">
                    <div class="relative flex-grow">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <button
                        v-if="tasks_due_date_range && tasks_due_date_range.start"
                        class="absolute right-0 flex items-center justify-center w-6 h-6 mx-2 rounded-full top-2 group hover:bg-gray-300 rounderd-full"
                        type="button"
                        @click="tasks_due_date_range = null">
                        <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
                      </button>

                      <input
                        :value="tasks_due_date_range && tasks_due_date_range.start ? `${inputValue.start} - ${inputValue.end}` : ''"
                        :placeholder="$t('Due date')"
                        class="w-full px-10 py-2 pl-8 pr-2 text-gray-900 bg-white border border-gray-300 rounded outline-none dark:bg-dark-box-background dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light"
                        @blur="hidePopover"
                        v-on="inputEvents.start || inputEvents.end"
                        @input="
                          $utils.debounce(() => {
                            tasks_due_date_range = $event.target.value
                          }, 300)
                        ">
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div
            v-if="!related_tasks_displayed?.length"
            class="relative w-full p-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
            <div class="text-lg">
              {{ $t("No tasks to display") }}
            </div>
            <div v-if="!tasks_filter_applied" class="mt-1 text-sm">
              {{ $t("Add new tasks to plan") }}
            </div>
            <div v-else class="mt-1 text-sm">
              {{ $t("No tasks matching filter") }}
            </div>
          </div>

          <div>
            <draggable
              v-model="related_tasks_displayed"
              as="div"
              static
              :class="[
                'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
                `grid ${fullScreen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8 overflow-visible mt-1 border-0 ${
                  $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
              ]"
              :component-data="{ type: 'transition-group' }"
              v-bind="{
                disabled: tasks_count > tasksPerPage,
                animation: 200,
                group: 'group',
                ghostClass: 'ghost',
                chosenClass: 'chosen'
              }"
              item-key="id"
              @end="endTaskDrag()">
              <template #item="{ element: task }">
                <div class="bg-white">
                  <div
                    v-if="expanded_tasks.includes(task.id)"
                    class="relative p-4 mb-4 bg-white border rounded-md dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white"
                    @mouseenter="handleTaskEnter(task)"
                    @mouseleave="handleTaskLeave">
                    <div class="flex items-center">
                      <ToggleStatusButton
                        :object="task"
                        :disable-toggle="task.filter"
                        @statusChanged="
                          ($event) => {
                            handleStatusChanged($event, task)
                          }
                        " />

                      <div class="ml-2">
                        {{ task.name }}
                      </div>
                      <div class="absolute flex -right-3 -top-3 gap-x-2">
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleExpandTask(task)">
                          <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                          <span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Minimize") }}</span>
                        </div>
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                          @click="handleViewTask(task)">
                          <Maximize2
                            class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                            color="#2DA7D7" />
                          <span
                            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                            {{ $t("View Task") }}</span>
                        </div>
                        <div
                          v-if="!editing_disabled"
                          class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleRemoveTask(task)">
                          <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Remove") }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex w-full mt-5">
                      <div class="w-1/3">
                        <div class="flex flex-row items-center gap-2">
                          <div class="text-common-grey">
                            {{ $t("Priority") }}:
                          </div>

                          <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                            <div v-if="task.priority" class="flex items-center capitalize gap-x-2">
                              <div>
                                {{ task.priority }}
                              </div>
                              <div>
                                <div v-if="task.priority == $t('normal')">
                                  <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                                  <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                                </div>
                                <div v-else-if="task.priority == $t('high')">
                                  <ChevronRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                </div>
                                <div v-else-if="task.priority == $t('highest')">
                                  <ChevronsRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                </div>
                                <div v-else-if="task.priority == $t('low')">
                                  <ChevronRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                </div>
                                <div v-else-if="task.priority == $t('lowest')">
                                  <ChevronsRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                </div>
                              </div>
                            </div>

                            <span v-else> - </span>
                          </div>
                        </div>
                      </div>

                      <div class="w-1/3">
                        <div class="flex flex-row items-center gap-2">
                          <div class="text-common-grey whitespace-nowrap">
                            {{ $t("Due Date") }}:
                          </div>

                          <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                            <span v-if="task.due_date">
                              {{ $moment(task.due_date).format("DD MMM  YYYY") }}
                            </span>

                            <span v-else> - </span>
                          </div>
                        </div>
                      </div>

                      <div :class="[`${task.assigned_to?.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                        <div
                          :class="[`${task.assigned_to?.length > 4 ? '' : '-ml-10'}`]"
                          class="mb-2 text-common-grey whitespace-nowrap text-start">
                          {{ $t("Team Members") }}
                        </div>
                        <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                          <span v-for="user in task.assigned_to.slice(0, 4)" :key="user.id" class="inline-block has-tooltip">
                            <img
                              v-if="$utils.getUserById(user, 'avatar')"
                              class="inline-block rounded-full h-9 w-9 object-cover"
                              :src="$utils.getUserById(user, 'avatar')"
                              alt="">
                            <Avatar
                              v-else
                              :first-name="$utils.getUserById(user, 'first_name')"
                              :last-name="$utils.getUserById(user, 'last_name')" />
                            <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                              <div class="">
                                <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                                  {{ $utils.getUserById(user, "full_name") }}
                                </p>
                              </div>
                            </span>
                          </span>
                          <span
                            v-if="task.assigned_to?.length > 4"
                            class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                            + {{ task.assigned_to?.length - 4 }} {{ $t("more") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else
                    class="relative flex p-4 mb-4 align-middle bg-white border rounded-md dark:bg-dark-box-background dark:border-dark-divider-border dark:text-white"
                    @mouseenter="handleTaskEnter(task)"
                    @mouseleave="handleTaskLeave">
                    <div
                      :class="[
                        'w-4 h-4 rounded-full mt-1 mr-4 relative',
                        {
                          'bg-status-warning-yellow': getActualStatus(task, task.status) == 'overdue',
                          'bg-dark-brand-blue': ['in-progress', 'pending'].includes(task.status),
                          'bg-status-success-green': task.status == 'completed',
                          'bg-status-error-red': ['cancelled', 'missed'].includes(task.status)
                        }
                      ]" />

                    <button v-if="!(tasks_count > tasksPerPage)" class="button hover:cursor-move mr-2">
                      <Tally2 class="h-5 w-5 text-common-grey rotate-90" aria-hidden="true" />
                    </button>
                    <div>{{ task.name }}</div>

                    <div class="absolute flex -right-3 -top-3 gap-x-2">
                      <div
                        class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                        @click="handleExpandTask(task)">
                        <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                        <span
                          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                          {{ $t("Expand") }}</span>
                      </div>
                      <div
                        class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                        @click="handleViewTask(task)">
                        <Maximize2
                          class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                          color="#2DA7D7" />
                        <span
                          class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                          {{ $t("View Task") }}</span>
                      </div>
                      <div
                        v-if="!editing_disabled"
                        class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                        @click="handleRemoveTask(task)">
                        <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                          class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                          {{ $t("Remove") }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>

          <div class="flex items-center w-full !-mt-4">
            <div
              v-if="tasks_count > 6 && tasks_count != tasksPerPage"
              class="mr-2 text-sm text-blue cursor-pointer"
              @click="tasksPerPage = tasks_count">
              {{ $t("Show all") }} ({{ tasks_count }})
            </div>
            <div v-else-if="tasks_count > 6" class="mr-2 text-sm text-blue cursor-pointer" @click="tasksPerPage = 6">
              {{ $t("Show less") }}
            </div>
            <Pagination
              v-if="tasks_count > tasksPerPage"
              class="ml-auto"
              :total-pages="tasks_total_pages"
              :current-page="tasksCurrentPage"
              @input="tasksChangePage" />
          </div>

          <div class="relative rounded-md shadow-sm border p-2 mt-2">
            <div class="flex items-center justify-between w-full">
              <label class="block text-common-grey">{{ $t("Tags") }}</label>
              <button class="primary-btn w-fit" @click.prevent="is_tags_modal_open = true">
                <Plus class="w-6 h-6 mr-2" aria-hidden="true" />
                <span>{{ selected_tags?.length ? $t("Edit Tags") : $t("Add Tags") }}</span>
              </button>
            </div>
            <div v-if="selected_tags?.length" class="flex flex-wrap gap-2 mt-4">
              <div
                v-for="tag in selected_tags"
                :key="tag?.id"
                class="flex focus:outline-none rounded-md"
                :style="{ background: all_tags.find((t) => t.id == tag.id)?.color }">
                <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
                  <span
                    class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
                    :style="{
                      background: all_tags.find((t) => t.id == tag.id)?.color
                    }">
                    <span class="block text-white truncate">{{ tag.name }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="is_adding_note || is_editing_note" class="px-2 pb-3 mt-5 border rounded-md shadow-sm dark:border-dark-divider-border">
            <div class="my-2 font-semibold border dark:border-dark-divider-border">
              <TextInput
                :disabled="editing_disabled"
                :no-border="true"
                name="note_title"
                :value="note_title"
                :is-box-background="true"
                type="text"
                :placeholder="$t('Title')"
                input-classes="border border-red-500 appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                @input="
                  $utils.debounce(() => {
                    note_title = $event.target.value
                  }, 800)
                " />
            </div>
            <div class="mt-0">
              <div class="h-24 mt-0 border-0">
                <TextEditor
                  :disabled="editing_disabled"
                  :value="note_text"
                  :show-label="false"
                  :no-border="true"
                  toolbar="essential"
                  :placeholder="$t('Note description')"
                  name="note_text"
                  :label="$t('Description')"
                  theme="snow"
                  input-classes="rounded-b-md"
                  @changed="
                    (e) => {
                      handleEditNoteText(e)
                    }
                  " />
              </div>
            </div>
            <div class="flex flex-row justify-end w-full gap-2 pt-12">
              <span class="w-1/2" />
              <button
                type="button"
                class="w-1/4 m-0 cancel-btn"
                @click="
                  () => {
                    is_adding_note = false
                    is_editing_note = false
                  }
                ">
                {{ $t("Cancel") }}
              </button>
              <button type="button" class="w-1/4 primary-btn" @click="saveNote">{{ $t("Save") }}</button>
            </div>
          </div>

          <div
            v-else-if="!editing_disabled"
            class="flex items-center content-center w-full p-2 mt-5 font-normal border border-solid rounded cursor-pointer text-common-grey dark:border-dark-divider-border"
            @click="handleAddNote">
            <NotebookPen
              :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
              class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration"
              @click="is_adding_note = true" />

            <div class="pl-1 ml-3">
              {{ $t("Write a new note") }}
            </div>
          </div>
          <div class="mt-2 text-sm text-red-500">
            {{ notes_error_msg }}
          </div>

          <div
            v-if="!is_editing_note"
            class="grid grid-cols-3 gap-3 ease-in-out rounded-lg shadow-none no-scrollbar dark:bg-dark-screen-background transition duration-side-menu-transition-duration">
            <div v-for="(note, index) in notes_displayed" :key="note">
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <div class="relative" @mouseenter="note_hover_index = index" @mouseleave="note_hover_index = null">
                  <div v-if="index == note_hover_index && !editing_disabled" class="absolute flex cursor-pointer right-2 gap-x-2 top-2">
                    <Pencil
                      :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                      class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration"
                      @click="editNote(note)" />
                    <Trash2
                      :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                      class="w-5 h-5 transition ease-in-out dark:text-white duration-side-menu-transition-duration"
                      @click="deleteNote(note)" />
                  </div>
                  <div class="p-3 bg-white border rounded-lg dark:border-dark-divider-border dark:bg-dark-box-background">
                    <div class="text-lg font-semibold dark:text-white">
                      {{ note.title }}
                    </div>
                    <TextEditor
                      :disabled="true"
                      :no-border="true"
                      :value="note.description"
                      toolbar="essential"
                      :name="`description${index}`"
                      label=""
                      theme="bubble" />
                    <!-- <div class="mt-3 mbn-2" v-html="note.description" /> -->
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <div class="flex items-center w-full">
            <div v-if="notes_count > 3" class="mr-2 text-sm text-common-grey">
              {{ $t("Show") }}
            </div>
            <SelectInput
              v-if="notes_count > 3"
              :value="notesPerPage"
              :is-box-background="true"
              class="w-[115px]"
              :input-classes="'text-left'"
              name="notes_page_show_options"
              :allow-null="false"
              :items="notes_page_show_options"
              @update:currentItem="notesPerPage = $event" />
            <div v-if="notes_count > 3" class="ml-2 text-sm text-common-grey">
              {{ $t("in page") }}
            </div>
            <Pagination
              v-if="notes_count > notesPerPage"
              class="ml-auto"
              :total-pages="notes_total_pages"
              :current-page="notesCurrentPage"
              @input="notesChangePage" />
          </div>

          <AttachmentsField
            v-model="attachments"
            v-model:deleted-attachments="deleted_attachments"
            v-model:new-attachments="new_attachments"
            :disabled="editing_disabled"
            open-position="left" />

          <div class="w-full flex flex-col items-end pt-2 text-status-error-red">
            <p v-for="error in errors" :key="error">
              {{ error }}
            </p>
          </div>

          <div v-if="template_error" class="flex flex-col items-end w-full pt-2 text-status-error-red">
            <p>
              {{ template_error }}
            </p>
          </div>

          <div v-if="!editing_disabled" class="flex flex-row justify-between w-full gap-2 pt-6">
            <button :class="{ 'w-1/2': inPlaybook }" type="button" class="w-1/3 cancel-btn" @click="handleClosePlan">
              {{ $t("Cancel") }}
            </button>
            <button
              v-if="!inPlaybook"
              type="button"
              class="w-1/3 delete-btn"
              @click="
                () => {
                  $emitter.emit(ALL_EMITTERS.DELETE_OBJECT, {
                    object: { ...plan, type: 'Plan' },
                    objectDeleteCallBack: () => {
                      $emit('cancel')
                    }
                  })
                }
              ">
              {{ $t("Delete") }}
            </button>
            <button :disabled="submitting_is_on" :class="{ 'w-1/2': inPlaybook }" type="submit" class="w-1/3 primary-btn">
              <RotateCw
                :class="{
                  'animate-spin': submitting_is_on,
                  'opacity-100': submitting_is_on
                }"
                class="w-5 h-5 mr-3 -ml-1 opacity-0"
                aria-hidden="true" />

              {{ isTemplate ? $t("Save Plan as Template") : inPlaybook ? $t("Save Plan") : $t("Update Plan") }}
            </button>
          </div>
        </Form>
      </div>
    </div>

    <!-- Delete Note -->
    <Modal v-model:is-open="is_delete_note_modal_open" :default-success-msg="$t('Note Deleted')">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Note") }} {{ note_to_be_deleted.title }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete a note, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeleteNote(showIsDone)">
            {{ $t("Delete note") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
      <template #done="{ close: closeModal }">
        <div>
          <div class="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full">
            <Check class="w-6 h-6 text-green-600" aria-hidden="true" />
          </div>
          <div class="mt-3 text-center sm:mt-5">
            <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
              {{ $t("Note deleted successfully") }}
            </h3>
          </div>
        </div>
        <div class="float-right mt-5 sm:mt-6">
          <button type="button" class="cancel-btn" @click="closeModal">{{ $t("Done") }}</button>
        </div>
      </template>
    </Modal>

    <!-- Add tags -->
    <Modal v-model:is-open="is_tags_modal_open" :default-success-msg="$t('Note Deleted')">
      <template #form="{ close: closeModal }">
        <TagsAutoCompleteInput
          :placeholder="$t('Add new tag')"
          value-attr="full_name"
          :selected-tags="selected_tags"
          @changed="
            ($event) => {
              handleTagsChanged($event)
            }
          "
          @close="closeModal" />
      </template>
    </Modal>
  </div>
</template>

<script>
import TagsAutoCompleteInput from "@/components/shared/TagsAutoCompleteInput.vue"
import { Switch } from "@headlessui/vue"
import * as Yup from "yup"
import Modal from "@/components/shared/Modal.vue"
import { DatePicker } from "v-calendar"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import { Form } from "vee-validate"
import DateTimeFrame from "@/components/shared/DateTimeFrame.vue"
import dataService from "@/services/data.service"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import AsyncAutoCompleteInput from "@/components/shared/AsyncAutoCompleteInput.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import {
  NotebookPen,
  Maximize2,
  ClipboardCheck,
  Funnel,
  ChevronsRight,
  ChevronRight,
  Share2,
  Search,
  X,
  Tally2,
  Check,
  Plus,
  Trash2,
  RotateCw,
  Pencil
} from "lucide-vue-next"
import Pagination from "@/components/shared/Pagination.vue"
import { Menu, MenuButton, MenuItems } from "@headlessui/vue"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import AsyncMultiSelectCompaniesInput from "@/components/shared/AsyncMultiSelectCompaniesInput.vue"
import AsyncMultiSelectContactsInput from "@/components/shared/AsyncMultiSelectContactsInput.vue"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import ToggleStatusButton from "../shared/ToggleStatusButton.vue"
import Avatar from "@/components/shared/Avatar.vue"
import { useStore } from "vuex"
import { Record } from "@/models/record"
import Badge from "@/components/shared/ActivityStatusBadge.vue"
import { markRaw } from "vue"
import { MODULES } from "@/store/constants/references"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import draggable from "vuedraggable"
import { useI18n } from "vue-i18n"

export default {
  components: {
    draggable,
    Plus,
    TagsAutoCompleteInput,
    Switch,
    Badge: markRaw(Badge),
    Tally2,
    //eslint-disable-next-line
    ClipboardCheck: markRaw(ClipboardCheck),
    DateTimeFrame,
    TextEditor,
    X,
    RotateCw,
    ContactsSelect,
    CompaniesSelect,
    TeamMembersSelect,
    AttachmentsField,
    Check,
    Modal,
    TextInput,
    DatePicker,
    SelectInput,
    Form,
    Maximize2,
    AsyncAutoCompleteInput,
    ChevronRight,
    ChevronsRight,
    Pagination,
    NotebookPen,
    Pencil,
    Trash2,
    Menu,
    MenuButton,
    MenuItems,
    Share2,
    MultiSelectInput,
    AsyncMultiSelectCompaniesInput,
    AsyncMultiSelectContactsInput,
    Funnel,
    Search,
    ToggleStatusButton,
    Avatar
  },

  inject: ["canUserEditLayout", "isModuleActivated"],

  props: {
    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },
    resultReferencesList: null,
    resultReferencesListForText: null,
    inPlaybook: {
      type: Boolean,
      default: false
    },
    isTemplate: {
      type: Boolean,
      default: false
    },
    plan: {
      type: Object,
      required: true
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    fromExpand: {
      type: Boolean,
      default: false
    }
  },

  emits: ["cancel", "submitted", "error", "done"],
  setup(props) {
    const store = useStore()
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Plan name is required")),
      description: Yup.string(),
      goal: Yup.string().nullable(),
      assigned_to: Yup.array().of(Yup.string()),
      type: Yup.string().required(t("Plan type is required"))
    })

    const plan_clone = { ...props.plan }

    let plan_name = plan_clone.name
    if (props.fromExpand) {
      plan_name = store.state.data.plan_data.plan_name
    }

    let type = plan_clone.type
    if (props.fromExpand) {
      type = store.state.data.plan_data.plan_type
    }

    let description = plan_clone.description || ""
    if (props.fromExpand) {
      description = store.state.data.plan_data.plan_description || ""
    }

    const initial_values = { name: plan_name, type, description }

    return {
      schema,
      initial_values
    }
  },

  data() {
    return {
      auto_set_start_date: false,
      list_to_get: "goals",
      list_to_get_task: "tasks",
      assign_collaborators_to_goals_and_tasks: false,
      selected_tags: [],
      all_tags: [],
      auto_compute_status: false,
      assign_companies_to_goals_and_tasks: false,
      action_name: null,
      submitting_is_on: false,
      ALL_EMITTERS,
      MODULES,
      task_icon: ClipboardCheck,
      Record,
      attachments: [],
      deleted_attachments: [],
      new_attachments: [],
      expanded_goals: [],
      expanded_tasks: [],
      isBoxBackgroud: false,
      types: [
        { label: this.$t("Account Plan"), value: "account_plan" },
        { label: this.$t("Growth Plan"), value: "growth_plan" },
        { label: this.$t("Success Plan"), value: "success_plan" },
        { label: this.$t("Onboarding Plan"), value: "onboarding_plan" },
        { label: this.$t("Health Plan"), value: "health_plan" },
        { label: this.$t("Renewal Plan"), value: "renewal_plan" },
        { label: this.$t("Other"), value: "other" }
      ],
      note_to_be_deleted: null,
      note_to_be_edited: null,
      is_delete_note_modal_open: false,
      is_tags_modal_open: false,
      note_title: null,
      note_text: null,
      is_adding_note: false,
      is_editing_note: false,
      selected_companies: [],
      selected_team_members: [],
      selected_contacts: [],
      due_date: null,
      start_date: null,
      relative_date: null,
      relative_date_text: null,
      inputDueDateIsFocused: false,
      inputStartDateIsFocused: false,
      is_modal_relative_date_open: false,
      goal_hover_id: null,
      task_hover_id: null,
      perPage: 6,
      currentPage: 1,
      related_goals_displayed: [],
      total_pages: 1,
      goals_count: 0,

      tasksPerPage: 6,
      tasksCurrentPage: 1,
      related_tasks_displayed: [],
      tasks_total_pages: 1,
      tasks_count: 0,

      notesPerPage: 3,
      notesCurrentPage: 1,
      notes_displayed: [],
      notes_total_pages: 1,
      notes_count: 0,

      note_hover_index: null,
      status: null,
      templates: [],
      template_error: "",

      //goals filters
      goals_status: null,
      search_term: "",
      goals_filter_active: false,
      goals_companies: [],
      goals_contacts: [],
      goals_assigned_to: [],
      goals_due_date_range: null,

      //tasks filters
      tasks_status: null,
      tasks_search_term: "",
      tasks_filter_active: false,
      tasks_companies: [],
      tasks_contacts: [],
      tasks_assigned_to: [],
      tasks_due_date_range: null,

      dont_fetch: false,
      notes_error_msg: "",

      plan_name: null,
      plan_description: null,
      plan_type: null
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    },

    filter_applied() {
      if (
        this.goals_status?.length ||
        this.search_term ||
        this.goals_companies?.length ||
        this.goals_contacts?.length ||
        this.goals_assigned_to?.length ||
        this.goals_due_date_range
      ) {
        return true
      } else {
        return false
      }
    },

    tasks_filter_applied() {
      if (
        this.tasks_status?.length ||
        this.tasks_search_term ||
        this.tasks_companies?.length ||
        this.tasks_contacts?.length ||
        this.tasks_assigned_to?.length ||
        this.tasks_due_date_range
      ) {
        return true
      } else {
        return false
      }
    },
    goals_filter_applied() {
      if (
        this.goals_status?.length ||
        this.goals_companies?.length ||
        this.goals_contacts?.length ||
        this.goals_assigned_to?.length ||
        this.goals_due_date_range
      ) {
        return true
      } else {
        return false
      }
    },
    notes_page_show_options() {
      const options = [3]
      if (this.notes_count > 3) {
        options.push(6)
      }
      if (this.notes_count > 6) {
        options.push(9)
      }
      if (this.notes_count > 9) {
        options.push(12)
      }
      if (this.notes_count > 12) {
        options.push(15)
      }
      return options
    }
  },

  watch: {
    status() {
      if (this.status == "in-progress" && this.auto_set_start_date == true && !this.start_date) {
        this.start_date = new Date()
      }
    },
    plan_name() {
      this.template_error = ""
    },
    perPage: {
      handler() {
        const params = { page_size: this.perPage, page: 1 }
        if (this.plan.id) {
          dataService.getPlanGoals(this.plan.id, params).then((res) => {
            this.currentPage = 1
            this.related_goals_displayed = res.data.results.map((g) => {
              return {
                ...g,
                filter: g.filter?.split(" ")[0]
                  ? g.filter.split(" ")[0]?.length > 1
                    ? `( ${g.filter.slice(1, g.filter.length - 1)} )`
                    : g.filter
                  : g.filter
              }
            })
            this.totalPages(res.data)
          })
        }
      }
    },

    tasksPerPage: {
      handler() {
        const params = { page_size: this.tasksPerPage, page: 1 }
        if (this.plan.id) {
          dataService.getPlanTasks(this.plan.id, params).then((res) => {
            this.tasksCurrentPage = 1
            this.related_tasks_displayed = res.data.results
            this.tasksTotalPages(res.data)
          })
        }
      }
    },
    notesPerPage: {
      handler() {
        const params = { page_size: this.notesPerPage, page: 1 }
        if (this.plan.id) {
          dataService.getPlanNotes(this.plan.id, params).then((res) => {
            this.notesCurrentPage = 1
            this.notes_displayed = res.data.results
            this.notesTotalPages(res.data)
          })
        }
      }
    },

    //goals filter watchers
    goals_status: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanGoals()
        }
      }
    },
    search_term: {
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanGoals()
        }
      }
    },
    goals_companies: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanGoals()
        }
      }
    },
    goals_contacts: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanGoals()
        }
      }
    },

    goals_assigned_to: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanGoals()
        }
      }
    },
    goals_due_date_range: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanGoals()
        }
      }
    },

    //tasks filter watchers
    tasks_status: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanTasks()
        }
      }
    },
    tasks_search_term: {
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanTasks()
        }
      }
    },
    tasks_companies: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanTasks()
        }
      }
    },
    tasks_contacts: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanTasks()
        }
      }
    },

    tasks_assigned_to: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanTasks()
        }
      }
    },
    tasks_due_date_range: {
      deep: true,
      handler() {
        if (!this.dont_fetch) {
          this.getFilteredPlanTasks()
        }
      }
    },
    relative_date() {
      if (this.relative_date) {
        if (this.relative_date.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.relative_date.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days from In Progress")
        }
        this.due_date = null
      }
    },
    due_date() {
      if (this.due_date) {
        this.relative_date_text = null
      }
    }
  },

  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  activated() {
    this.$forceUpdate()
    this.register_listeners()
  },
  created() {
    const params = { source_object_type: "plan" }
    dataService.getTemplates(params).then((res) => {
      this.templates = res.data.results
    })
    this.register_listeners()

    if (this.plan) {
      if (this.plan?.auto_start_date_set) {
        this.auto_set_start_date = this.plan?.auto_start_date_set
      }

      if (this.plan.goals) {
        this.totalPages(this.plan.goals)
      }

      if (this.plan.tasks) {
        this.tasksTotalPages(this.plan.tasks)
      }

      if (this.plan.notes) {
        this.notesTotalPages(this.plan.notes)
      }

      this.auto_compute_status = !!this.plan.show_dynamic_status
      if (this.plan.tags?.length > 0) {
        this.all_tags = this.$store.state.data.actions_tags
        this.all_tags.map((tag) => {
          if (this.plan.tags.find((t) => t == tag.id)) {
            this.selected_tags.push(tag)
          }
        })
      } else {
        this.selected_tags = []
      }
      this.plan_name = this.plan.name
      this.plan_description = this.plan.description
      this.plan_type = this.plan.type
      this.assign_companies_to_goals_and_tasks = this.plan.assign_companies_to_goals_and_tasks
      this.assign_collaborators_to_goals_and_tasks = this.plan.assign_collaborators_to_goals_and_tasks

      if (!this.isTemplate) {
        this.selected_companies = this.plan.companies
        this.selected_contacts = this.plan.contacts
        this.selected_team_members = this.plan.collaborators
      }

      this.status = this.plan.status

      if (this.inPlaybook) {
        const att_calc = []
        this.plan.attachments?.forEach((attachment_id) => {
          dataService.getAttachment(attachment_id).then((res) => {
            att_calc.push(res.data)
            if (att_calc?.length == this.plan.attachments?.length) {
              this.attachments = att_calc
            }
          })
        })
      } else if (this.plan.attachments) {
        this.attachments = this.plan.attachments.results
      }
    }

    if (this.plan?.goals?.length || this.plan?.goals?.results?.length) {
      if (this.inPlaybook || this.isTemplate) {
        this.related_goals_displayed = []
        this.plan.goals.results.forEach((goal_id) => {
          if (goal_id?.name) {
            if (!goal_id?.id) {
              goal_id["for_template"] = true
              goal_id["id"] = this.$uuid()
            }
            this.related_goals_displayed.push(goal_id)
          } else {
            dataService.getGoal(goal_id).then((res) => {
              this.related_goals_displayed.push({
                ...res.data,

                filter: res.data.filter?.split(" ")[0]
                  ? res.data.filter.split(" ")[0]?.length > 1
                    ? `( ${res.data.filter.slice(1, res.data.filter.length - 1)} )`
                    : res.data.filter
                  : res.data.filter
              })
            })
          }
        })
      } else {
        this.related_goals_displayed = this.plan.goals.results.slice(0, this.tasksPerPage)
      }
    }

    if (this.plan?.tasks?.length || this.plan?.tasks?.results?.length) {
      if (this.inPlaybook) {
        this.related_tasks_displayed = []
        this.plan.tasks.forEach((task_id) => {
          if (task_id?.name) {
            if (!task_id?.id) {
              task_id["for_template"] = true
              task_id["id"] = this.$uuid()
            }
            this.related_tasks_displayed.push(task_id)
          } else {
            dataService.getTask(task_id).then((res) => {
              this.related_tasks_displayed.push(res.data)
            })
          }
        })
      } else {
        this.related_tasks_displayed = this.plan.tasks.results.slice(0, this.tasksPerPage)
      }
    }

    if (this.plan?.notes) {
      this.notes_displayed = this.plan?.notes.results
    }

    if (this.plan?.due_date) {
      this.due_date = this.$moment.utc(this.plan?.due_date).local().toDate()
    }

    if (this.plan?.start_date) {
      this.start_date = this.$moment.utc(this.plan?.start_date).local().toDate()
    }

    if (
      this.plan?.due_date_relative_to &&
      (this.inPlaybook ||
        (this.plan?.due_date_relative_to == "start_date" && !this.plan?.start_date) ||
        (this.plan?.due_date_relative_to == "in_progress_date" && this.plan?.status !== this.$t("in-progress")))
    ) {
      this.relative_date = {
        due_date_relative_to: this.plan?.due_date_relative_to,
        due_date_days_offset: this.plan?.due_date_days_offset
      }
    }
    this.populatePlanData()
  },

  methods: {
    getActualStatus(object, status) {
      if (object.due_date) {
        if (
          (status == "pending" || status == "in-progress") &&
          this.$moment(object.due_date).local().startOf("day").isBefore(this.$moment().startOf("day"))
        ) {
          return "overdue"
        }
        return status
      }
      return status
    },
    handleStatusChanged(status, item) {
      item.status = status
    },
    endTaskDrag() {
      this.$store.dispatch("data/updatePlan", { plan: { id: this.plan.id, tasks: this.related_tasks_displayed.map((task) => task.id) } })
    },
    endGoalDrag() {
      this.$store.dispatch("data/updatePlan", { plan: { id: this.plan.id, goals: this.related_goals_displayed.map((goal) => goal.id) } })
    },
    handleTagsChanged(tags) {
      this.all_tags = this.$store.state.data.actions_tags
      this.selected_tags = tags
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.CONFIRM_ADD_GOAL_TASKS_TO_PLAN_UPDATE_FORM, (goals) => {
        const goal = goals[0]
        const goal_tasks = goal.tasks.results

        let page = 2

        //first remove first 10 tasks and then add them to make sure no tasks added before
        dataService.removeTaskFromPlan(this.plan.id, { tasks_ids: goal_tasks?.map((g) => g.id) }).then(() => {
          dataService.addTaskToPlan(this.plan.id, { tasks_ids: goal_tasks?.map((g) => g.id) }).then(() => {
            //then if there is more than 10 fetch the rest using a recurring function and refetch plan's task first page after all is fetched
            if (goal.tasks.results?.length < goal.tasks.count) {
              const addTasksToPlan = () => {
                const params = { page_size: 10, page: page }
                dataService.getGoalTasks(goal.id, params).then((res) => {
                  dataService
                    .removeTaskFromPlan(this.plan.id, {
                      tasks_ids: res.data.results.map((g) => g.id)
                    })
                    .then(() => {
                      dataService
                        .addTaskToPlan(this.plan.id, {
                          tasks_ids: res.data.results.map((g) => g.id)
                        })
                        .then(() => {
                          if (res.data.next) {
                            page += 1
                            addTasksToPlan()
                          } else {
                            this.tasksChangePage(1, true)
                          }
                        })
                    })
                })
              }
              addTasksToPlan()
            } else {
              this.tasksChangePage(1, true)
            }
          })
        })
      })

      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN_UPDATE_FORM, (goals) => {
        this.handleGoalSelected(null, () => {}, goals)
      })
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN_UPDATE_FORM, (tasks) => {
        this.handleTaskSelected(null, () => {}, tasks)
      })
      this.$emitter.on(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_UPDATE_FORM, (goal) => {
        let index = this.related_goals_displayed.findIndex((g) => g.id == goal.id)
        this.related_goals_displayed.splice(index, 1, goal)
      })
      this.$emitter.on(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_UPDATE_FORM, (task) => {
        let index = this.related_tasks_displayed.findIndex((t) => t.id == task.id)
        this.related_tasks_displayed.splice(index, 1, task)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.CONFIRM_ADD_GOAL_TASKS_TO_PLAN_UPDATE_FORM)
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN_UPDATE_FORM)
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN_UPDATE_FORM)
      this.$emitter.off(ALL_EMITTERS.GOAL_UPDATED_IN_PLAN_UPDATE_FORM)

      this.$emitter.off(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_UPDATE_FORM)
    },
    handleRestoreStatus() {
      if (this.plan.status != this.status) {
        const data = {}
        data["plan"] = {
          id: this.plan.id,
          record_full: this.plan.record,
          status: this.status,
          dont_load: true
        }
        this.$store.dispatch("data/updatePlan", data).then(() => {})
      }
    },

    populatePlanData() {
      if (this.fromExpand) {
        Object.keys(this.$data).forEach((key) => {
          this[key] = this.$store.state.data.plan_data[key]
        })
        if (!this.fullScreen) {
          this.goals_filter_active = false
          this.resetFilters()
          this.tasks_filter_active = false
          this.tasksResetFilters()
        }
      }
    },
    handleGoalsCompaniesSelected(e) {
      this.goals_companies = e
    },

    handleGoalsContactsSelected(e) {
      this.goals_contacts = e
    },

    handleTasksCompaniesSelected(e) {
      this.tasks_companies = e
    },

    handleTasksContactsSelected(e) {
      this.tasks_contacts = e
    },
    getFilteredPlanGoals() {
      let params = {
        page_size: this.perPage,
        expand: "record",
        ...(!!this.goals_assigned_to && {
          assigned_to: this.goals_assigned_to.join(",")
        }),
        ...(!!this.goals_status && { status: this.goals_status.join(",") }),
        ...(!!this.goals_companies?.length && {
          record: this.goals_companies.map((c) => c.id).join(",")
        }),
        ...(!!this.goals_contacts && {
          contacts: this.goals_contacts.map((c) => c.id).join(",")
        })
      }
      // params["prevent_loading"] = true
      if (this.search_term) {
        params["name"] = this.search_term
      }
      params["plans"] = this.plan.id
      if (this.goals_due_date_range) {
        params["due_date_start"] = this.$moment(this.goals_due_date_range.start).format("L")
        params["due_date_end"] = this.$moment(this.goals_due_date_range.end).format("L")
      }
      this.$store.dispatch("data/getGoals", params).then((res) => {
        this.totalPages(res.data)
        this.related_goals_displayed = res.data.results?.map((g) => {
          return {
            ...g,
            filter: g.filter?.split(" ")[0]
              ? g.filter.split(" ")[0]?.length > 1
                ? `( ${g.filter.slice(1, g.filter.length - 1)} )`
                : g.filter
              : g.filter
          }
        })
      })
    },
    getFilteredPlanTasks() {
      let params = {
        page_size: this.tasksPerPage,
        expand: "record",
        ...(!!this.tasks_assigned_to && {
          assigned_to: this.tasks_assigned_to.join(",")
        }),
        ...(!!this.tasks_status && { status: this.tasks_status.join(",") }),
        ...(!!this.tasks_companies?.length && {
          record: this.tasks_companies.map((c) => c.id).join(",")
        }),
        ...(!!this.tasks_contacts && {
          contacts: this.tasks_contacts.map((c) => c.id).join(",")
        })
      }
      // params["prevent_loading"] = true
      if (this.tasks_search_term) {
        params["name"] = this.tasks_search_term
      }
      params["plans"] = this.plan.id
      if (this.tasks_due_date_range) {
        params["due_date_start"] = this.$moment(this.tasks_due_date_range.start).format("L")
        params["due_date_end"] = this.$moment(this.tasks_due_date_range.end).format("L")
      }
      this.$store.dispatch("data/getTasks", params).then((res) => {
        this.tasksTotalPages(res.data)
        this.related_tasks_displayed = res.data.results?.map((g) => {
          return {
            ...g,
            filter: g.filter?.split(" ")[0]
              ? g.filter.split(" ")[0]?.length > 1
                ? `( ${g.filter.slice(1, g.filter.length - 1)} )`
                : g.filter
              : g.filter
          }
        })
      })
    },

    handleFilterGoals() {
      this.goals_filter_active = !this.goals_filter_active
      if (!this.fullScreen && this.goals_filter_active) {
        this.handleExpandPlan()
      }

      if (!this.goals_filter_active && this.filter_applied) {
        this.resetFilters()
      }
    },

    handleFilterTasks() {
      this.tasks_filter_active = !this.tasks_filter_active
      if (!this.fullScreen && this.tasks_filter_active) {
        this.handleExpandPlan()
      }

      if (!this.tasks_filter_active && this.tasks_filter_applied) {
        this.tasksResetFilters()
      }
    },

    resetFilters() {
      const params = { page_size: this.perPage, page: 1 }
      dataService.getPlanGoals(this.plan.id, params).then((res) => {
        this.related_goals_displayed = res.data.results.map((g) => {
          return {
            ...g,
            filter: g.filter?.split(" ")[0]
              ? g.filter.split(" ")[0]?.length > 1
                ? `( ${g.filter.slice(1, g.filter.length - 1)} )`
                : g.filter
              : g.filter
          }
        })
        this.totalPages(res.data)
      })
      this.dont_fetch = true
      setTimeout(() => {
        this.dont_fetch = false
      }, 500)
      this.goals_status = null
      this.search_term = ""
      this.goals_companies = []
      this.goals_assigned_to = []
      this.goals_due_date_range = null
      this.currentPage = 1
    },

    tasksResetFilters() {
      const params = { page_size: this.tasksPerPage, page: 1 }
      dataService.getPlanTasks(this.plan.id, params).then((res) => {
        this.related_tasks_displayed = res.data.results
        this.tasksTotalPages(res.data)
      })
      this.dont_fetch = true
      setTimeout(() => {
        this.dont_fetch = false
      }, 500)
      this.tasks_status = null
      this.tasks_search_term = ""
      this.tasks_companies = []
      this.tasks_assigned_to = []
      this.tasks_due_date_range = null
      this.tasksCurrentPage = 1
    },

    handleAddNote() {
      this.is_adding_note = true
      this.note_title = null
      this.note_text = null
    },
    changePage(page, calcTotalPages) {
      this.currentPage = page
      let params = null
      params = { page_size: this.perPage, page: page }

      if (this.filter_applied) {
        params = {
          page_size: this.perPage,
          page: page,
          expand: "record",
          ...(!!this.goals_assigned_to && {
            assigned_to: this.goals_assigned_to.join(",")
          }),
          ...(!!this.goals_status && { status: this.goals_status.join(",") }),
          ...(!!this.goals_companies?.length && {
            record: this.goals_companies.map((c) => c.id).join(",")
          }),
          ...(!!this.goals_contacts && {
            contacts: this.goals_contacts.map((c) => c.id).join(",")
          })
        }
        if (this.search_term) {
          params["name"] = this.search_term
        }
        params["plans"] = this.plan.id
        if (this.goals_due_date_range) {
          params["due_date_start"] = this.$moment(this.goals_due_date_range.start).format("L")
          params["due_date_end"] = this.$moment(this.goals_due_date_range.end).format("L")
        }
      } else {
        params = { page_size: this.perPage, page: page }
      }

      if (this.filter_applied) {
        this.$store.dispatch("data/getGoals", params).then((res) => {
          this.related_goals_displayed = res.data.results?.map((g) => {
            return {
              ...g,
              filter: g.filter?.split(" ")[0]
                ? g.filter.split(" ")[0]?.length > 1
                  ? `( ${g.filter.slice(1, g.filter.length - 1)} )`
                  : g.filter
                : g.filter
            }
          })
          if (calcTotalPages) {
            this.totalPages(res.data)
          }
        })
      } else {
        dataService.getPlanGoals(this.plan.id, params).then((res) => {
          this.related_goals_displayed = res.data.results.map((g) => {
            return {
              ...g,
              filter: g.filter?.split(" ")[0]
                ? g.filter.split(" ")[0]?.length > 1
                  ? `( ${g.filter.slice(1, g.filter.length - 1)} )`
                  : g.filter
                : g.filter
            }
          })
          if (calcTotalPages) {
            this.totalPages(res.data)
          }
        })
      }
    },

    tasksChangePage(page, calcTotalPages) {
      this.tasksCurrentPage = page
      const params = { page_size: this.tasksPerPage, page: page }
      dataService.getPlanTasks(this.plan.id, params).then((res) => {
        this.related_tasks_displayed = res.data.results
        if (calcTotalPages) {
          this.tasksTotalPages(res.data)
        }
      })
    },

    notesChangePage(page, calcTotalPages) {
      this.notesCurrentPage = page
      const params = { page_size: this.notesPerPage, page: page }
      dataService.getPlanNotes(this.plan.id, params).then((res) => {
        this.notes_displayed = res.data.results
        if (calcTotalPages) {
          this.notesTotalPages(res.data)
        }
      })
    },

    totalPages(goals) {
      this.total_pages = Math.ceil(parseInt(goals.count) / parseInt(this.perPage))
      this.goals_count = goals.count
    },

    tasksTotalPages(tasks) {
      this.tasks_total_pages = Math.ceil(parseInt(tasks.count) / parseInt(this.tasksPerPage))
      this.tasks_count = tasks.count
    },

    notesTotalPages(notes) {
      this.notes_total_pages = Math.ceil(parseInt(notes.count) / parseInt(this.notesPerPage))
      this.notes_count = notes.count
    },

    handleGoalEnter(goal) {
      this.$utils.debounce(() => {
        this.goal_hover_id = goal.id
      }, 600)
    },
    handleGoalLeave() {
      this.goal_hover_id = null
      this.$utils.debounce(() => {
        this.goal_hover_id = null
      }, 600)
    },

    handleTaskEnter(task) {
      this.$utils.debounce(() => {
        this.task_hover_id = task.id
      }, 600)
    },
    handleTaskLeave() {
      this.task_hover_id = null
      this.$utils.debounce(() => {
        this.task_hover_id = null
      }, 600)
    },

    handleGoalSelected(goal, close, goals) {
      if (goal?.content) {
        goal = goal.content
        goal.for_template = true

        if (goal?.id) {
          goal["id"] = this.$uuid()
        }
      }

      let tasks_count = 0
      const goals_with_tasks = []
      let multiple_goals = false
      if (goal && goal.tasks?.count) {
        tasks_count = goal.tasks.count
        goals_with_tasks.push(goal)
      } else if (goals?.length) {
        multiple_goals = true
        goals.forEach((goal) => {
          tasks_count = 0
          goals_with_tasks.push(goal)
          if (goal && goal.tasks?.count) {
            tasks_count += goal.tasks.count
          }
        })
      }
      if (tasks_count && !this.inPlaybook) {
        this.$emitter.emit(ALL_EMITTERS.ADD_GOAL_TASKS_TO_PLAN, {
          tasks_count,
          multiple_goals,
          goals_with_tasks
        })
      }

      if (this.inPlaybook || this.isTemplate) {
        // this.changePage(1, true)

        this.related_goals_displayed.push({
          ...goal,

          filter: goal.filter?.split(" ")[0]
            ? goal.filter.split(" ")[0]?.length > 1
              ? `( ${goal.filter.slice(1, goal.filter.length - 1)} )`
              : goal.filter
            : goal.filter
        })
      } else {
        dataService
          .addGoalToPlan(this.plan.id, {
            goals_ids: goals ? goals.map((g) => g.id) : [goal.id]
          })
          .then(() => {
            this.changePage(1, true)
          })
      }
      close()
    },

    handleTaskSelected(task, close, tasks) {
      if (task?.content) {
        task = task.content
        task.for_template = true
        if (task?.id) {
          task["id"] = this.$uuid()
        }
      }

      if (this.inPlaybook || this.isTemplate) {
        this.related_tasks_displayed.push(task)
      } else {
        dataService
          .addTaskToPlan(this.plan.id, {
            tasks_ids: tasks ? tasks.map((g) => g.id) : [task.id]
          })
          .then(() => {
            this.tasksChangePage(1, true)
          })
      }

      close()
    },

    handleCreateGoal() {
      this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
        inPlan: true,
        records: this.selected_companies
      })
    },
    handleCreateTask() {
      this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
        inPlan: true,
        records: this.selected_companies
      })
    },
    handleExpandGoal(goal) {
      let index = this.expanded_goals.findIndex((g) => g == goal.id)
      if (index !== -1) {
        this.expanded_goals.splice(index, 1)
      } else {
        this.expanded_goals.push(goal.id)
      }
    },

    handleExpandTask(task) {
      let index = this.expanded_tasks.findIndex((t) => t == task.id)
      if (index !== -1) {
        this.expanded_tasks.splice(index, 1)
      } else {
        this.expanded_tasks.push(task.id)
      }
    },
    handleViewGoal(goal) {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_GOAL, {
        goal: this.$utils.clone(goal),
        in_playbook: this.inPlaybook,
        is_template: this.isTemplate
      })
    },
    handleViewTask(task) {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_TASK, {
        task: this.$utils.clone(task),
        in_playbook: this.inPlaybook,
        is_template: this.isTemplate
      })
    },
    handleRemoveGoal(goal) {
      if (this.inPlaybook) {
        this.related_goals_displayed = this.related_goals_displayed.filter((t) => t.id !== goal.id)
      } else {
        dataService.removeGoalFromPlan(this.plan.id, { goals_ids: [goal.id] }).then(() => {
          if (this.related_goals_displayed?.length <= 1 && this.currentPage > 1) {
            this.changePage(this.currentPage - 1, true)
          } else {
            this.changePage(this.currentPage, true)
          }
        })
      }
    },
    handleRemoveTask(task) {
      if (this.inPlaybook) {
        this.related_tasks_displayed = this.related_tasks_displayed.filter((t) => t.id !== task.id)
      } else {
        dataService.removeTaskFromPlan(this.plan.id, { tasks_ids: [task.id] }).then(() => {
          if (this.related_tasks_displayed?.length <= 1 && this.tasksCurrentPage > 1) {
            this.tasksChangePage(this.tasksCurrentPage - 1, true)
          } else {
            this.tasksChangePage(this.tasksCurrentPage, true)
          }
        })
      }
    },
    sharePlan(plan) {
      this.$emitter.emit(ALL_EMITTERS.SHARE_OBJECT, {
        object: { ...plan, type: "Plan" },
        objectDeleteCallBack: () => {
          this.$emit("cancel")
        },
        show_confirmation: true
      })
    },
    handleExpandPlan() {
      if (this.fullScreen) {
        this.$router.go(-1)
        this.$emitter.emit(ALL_EMITTERS.UPDATE_PLAN, {
          plan: this.$utils.clone(this.plan),
          from_expand: true
        })
      } else {
        this.$router.push(`/plans/${this.plan.id}`)
        this.$emit("cancel")
      }
      this.$store.dispatch("data/setPlanExpandData", { ...this.$data })
    },

    handleClosePlan() {
      //this.handleRestoreStatus()
      this.$emit("cancel")
    },

    handleSetConmpaniesList(e) {
      this.selected_companies = e
    },

    editNote(note) {
      this.note_to_be_edited = note
      this.is_editing_note = true
      this.note_title = note.title
      this.note_text = note.description
    },
    deleteNote(note) {
      this.note_to_be_deleted = note
      this.is_delete_note_modal_open = true
    },
    confirmDeleteNote(showIsDone) {
      dataService.removeNoteFromPlan(this.plan.id, { notes_ids: [this.note_to_be_deleted.id] }).then(() => {
        if (this.notes_displayed?.length <= 1 && this.notesCurrentPage > 1) {
          this.notesChangePage(this.notesCurrentPage - 1, true)
        } else {
          this.notesChangePage(this.notesCurrentPage, true)
        }
        showIsDone()
      })
    },

    handleEditNoteText(e) {
      this.note_text = e.replace(/href="(?!http)/, 'href="http://')
    },
    saveNote() {
      this.notes_error_msg = ""
      if (!this.note_title) {
        this.notes_error_msg = this.$t("note title missing")
      } else if (!this.note_text) {
        this.notes_error_msg = this.$t("note description missing")
      } else {
        if (this.is_adding_note) {
          dataService
            .createUserNote(this.$store.state.auth.user.id, {
              title: this.note_title,
              description: this.note_text
            })
            .then((res) => {
              dataService.addNoteToPlan(this.plan.id, { notes_ids: [res.data.id] }).then(() => {
                this.notesChangePage(1, true)
                this.$store.dispatch("data/updatePlan", { plan: { id: this.plan.id } })
                this.notesTotalPages(this.plan.notes)
              })
            })
          this.is_adding_note = false
        } else if (this.is_editing_note) {
          dataService
            .updateUserNote(this.$store.state.auth.user.id, this.note_to_be_edited.id, {
              title: this.note_title,
              description: this.note_text
            })
            .then(() => {
              let index = this.notes_displayed.findIndex((n) => n.id == this.note_to_be_edited.id)
              this.notes_displayed.splice(index, 1, {
                description: this.note_text,
                title: this.note_title
              })
              this.is_editing_note = false
              this.$store.dispatch("data/updatePlan", { plan: { id: this.plan.id } })
              this.notesTotalPages(this.plan.notes)
            })
        }
      }
    },

    focusStartDateInput() {
      this.inputStartDateIsFocused = !this.inputStartDateIsFocused
    },
    focusDueDateInput() {
      this.inputDueDateIsFocused = !this.inputDueDateIsFocused
    },
    clearDate() {
      this.$refs.start_date.hidePopover()
    },
    emptyStartDate() {
      this.start_date = null
      this.$refs.start_date.hidePopover()
    },
    emptyDueDate() {
      this.due_date = null
      this.relative_date = null
      this.relative_date_text = null
      this.relative_date_text = null
      this.$refs.due_date.hidePopover()
      this.is_modal_relative_date_open = false
    },
    editPlan(values) {
      let is_template_flag = false
      let data = { ...values }
      if (this.isTemplate) {
        if (this.templates.find((t) => t.name == this.plan_name)) {
          this.template_error = this.$t("Template with this name already exists")
          return
        } else {
          this.submitting_is_on = true

          data["name"] = this.plan_name
          data["source_object_id"] = this.plan.id
          data["source_object_type"] = "plan"

          is_template_flag = true
        }
      }
      this.submitting_is_on = true

      let params = {
        page_size: 6,
        goals_page_size: 6,
        tasks_page_size: 6,
        notes_page_size: 3
      }

      data["id"] = this.plan?.id
      data["companies"] = this.selected_companies.map((x) => (x.id ? x.id : x.value ? x.value : x))
      data["collaborators"] = this.selected_team_members.map((x) => (x.id ? x.id : x.value ? x.value : x))
      data["contacts"] = this.selected_contacts.map((x) => (x?.id ? x.id : x))
      data["description"] = this.plan_description
      data["show_dynamic_status"] = this.auto_compute_status
      data["tags"] = this.selected_tags.map((t) => t.id)
      data["organization"] = this.$store.state.organization.id
      data["auto_start_date_set"] = this.auto_set_start_date

      if (!this.auto_compute_status) {
        data["status"] = this.status == "overdue" ? "pending" : this.status
      }

      if (data["status"] == "overdue") {
        data["status"] = "pending"
      }

      if (this.start_date) {
        data["start_date"] = this.$moment(this.start_date)

          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["start_date"] = null
      }

      if (this.relative_date) {
        data["due_date_relative_to"] = this.relative_date.due_date_relative_to
        data["due_date_days_offset"] = this.relative_date.due_date_days_offset
        data["due_date"] = null
      } else if (this.due_date) {
        data["due_date"] = this.$moment(this.due_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      } else {
        data["due_date"] = null
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      }

      if (this.inPlaybook || this.isTemplate) {
        this.submitting_is_on = false

        const clean_goals = []
        const clean_tasks = []
        this.related_goals_displayed.forEach((g) => {
          const goal_clone = this.$utils.clone(g)

          if (this.plan.template_id) {
            delete goal_clone.id
          }

          goal_clone["assigned_to"] = goal_clone["assigned_to"]?.map((g) => (g.value ? g.value : g.id ? g.id : g)) || []
          goal_clone["record"] = goal_clone["record"]?.id ? goal_clone["record"].id : goal_clone["record"]

          if (!goal_clone["due_date_relative_to"]) {
            delete goal_clone["due_date_relative_to"]
          }

          if (goal_clone["tasks"]?.results) {
            goal_clone["tasks"] = goal_clone["tasks"].results
          }

          if (goal_clone["attachments"]?.results) {
            goal_clone["attachments"] = goal_clone["attachments"].results
          }

          if (!goal_clone["record"]) {
            delete goal_clone["record"]
          }

          if (!goal_clone["filter"]) {
            delete goal_clone["filter"]
          }

          if (goal_clone["notes"]?.results) {
            goal_clone["notes"] = goal_clone["notes"].results
          }

          goal_clone["tasks"] = []

          if (this.assign_companies_to_goals_and_tasks) {
            if (data?.companies.length) {
              goal_clone["record"] = this.selected_companies.map((x) => (x.id ? x.id : x))[0]
            }
          }

          clean_goals.push(goal_clone.id && !this.plan.template_id && !this.isTemplate ? goal_clone.id : goal_clone)
        })
        this.related_tasks_displayed.forEach((g) => {
          const task_clone = this.$utils.clone(g)
          if (this.plan.template_id) {
            delete task_clone.id
          }

          task_clone["assigned_to"] = task_clone["assigned_to"].map((g) => (g.value ? g.value : g.id ? g.id : g)) || []
          task_clone["record"] = task_clone["record"]?.id ? task_clone["record"].id : task_clone["record"]

          if (!task_clone["due_date_relative_to"]) {
            delete data["due_date_relative_to"]
          }

          if (!task_clone["due_date_relative_to"]) {
            delete task_clone["due_date_relative_to"]
          }

          if (task_clone["attachments"]?.results) {
            task_clone["attachments"] = task_clone["attachments"].results
          }

          if (!task_clone["record"]) {
            delete task_clone["record"]
          }

          if (task_clone["notes"]?.results) {
            task_clone["notes"] = task_clone["notes"].results
          }

          if (this.assign_companies_to_goals_and_tasks) {
            if (data?.companies.length) {
              task_clone["record"] = this.selected_companies.map((x) => (x.id ? x.id : x))[0]
            }
          }

          if (data?.collaborators?.length) {
            task_clone["collaborators"] = this.selected_team_members.map((x) => (x.id ? x.id : x))
          }

          clean_tasks.push(task_clone.id && !this.plan.template_id && !this.isTemplate ? task_clone.id : task_clone)
        })

        if (this.plan.template_id) {
          data["template_id"] = this.plan.template_id
        }

        data["goals"] = clean_goals
        data["tasks"] = clean_tasks
        data["assign_companies_to_goals_and_tasks"] = this.assign_companies_to_goals_and_tasks

        data["assign_collaborators_to_goals_and_tasks"] = this.assign_collaborators_to_goals_and_tasks
        data["show_dynamic_status"] = this.auto_compute_status

        if (!data["due_date_relative_to"]) {
          delete data["due_date_relative_to"]
        }

        if (this.relative_date) {
          data["due_date_relative_to"] = this.relative_date.due_date_relative_to
          data["due_date_days_offset"] = this.relative_date.due_date_days_offset
          data["due_date"] = null
        } else if (this.due_date) {
          data["due_date"] = this.$moment(this.due_date)
            .set("hour", this.$moment().hour())
            .set("minute", this.$moment().minute())
            .set("second", this.$moment().second())
            .toISOString()
          data["due_date_relative_to"] = null
          data["due_date_days_offset"] = 0
        } else {
          data["due_date"] = null
          data["due_date_relative_to"] = null
          data["due_date_days_offset"] = 0
        }

        if (this.attachments?.length) {
          const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
          const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

          let attachment_calc = old_attachments.map((att) => att.id)

          if (new_attachments?.length) {
            dataService.addAttachment({ attachments: new_attachments }).then((res) => {
              attachment_calc = attachment_calc.concat(res.data.map((a) => a.id))
              data["attachments"] = attachment_calc

              this.$emit("done", data)
            })
          } else {
            data["attachments"] = attachment_calc
            this.$emit("done", data)
          }
        } else {
          this.$emit("done", data)
        }
      } else {
        this.$store
          .dispatch("data/updatePlan", { plan: data, params })
          .then((res) => {
            if (this.new_attachments?.length) {
              let formData = new FormData()
              this.new_attachments.forEach((attachment, i) => {
                Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
              })
              this.$store.dispatch("data/addAttachmentsToPlan", {
                id: res.id,
                attachments: formData,
                params
              })
            }
            if (this.deleted_attachments?.length) {
              this.$store.dispatch("data/deleteAttachmentsFromPlan", {
                id: res.id,
                attachments_ids: this.deleted_attachments.map((x) => x.id),
                params
              })
            }

            this.$emitter.emit(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED, this.due_date ? new Date(this.due_date) : null)
            this.$emit("done", res)
            this.submitting_is_on = false
          })
          .catch(() => {
            this.submitting_is_on = false
            // To do show exceptions
            this.$emit("error")
          })
      }
      if (is_template_flag) {
        dataService.createTemplate({ ...data, content: data }).then((res) => {
          this.$emitter.emit(ALL_EMITTERS.NEW_ACTIVITY_TEMPLATE_ADDED, res.data)
          this.submitting_is_on = false
          this.$emit("done")
        })
      }
    }
  }
}
</script>
